/**
 * Membership tier mappings, shared by the backend and the frontend.
 *
 * NOTE: this file is deliberately plain JavaScript despite its `.ts` extension -
 * no type annotations, no enums, no `as const`, no `export type`. The frontend
 * deep-imports it (`@optimus/utils/src/membershipTierMaps`), and Create React
 * App's Webpack resolves a workspace package as an untranspiled dependency and
 * parses it without the TypeScript plugin, so any TypeScript-only syntax here
 * breaks the frontend build. `constant.ts` carries the same restriction; anything
 * needing real types belongs in one of the typed facades below instead.
 *
 * Keys are `SubscriptionTier` values ('edge', 'am', 'srt', ...) and, for the
 * rank maps, the level names. They are spelled as literals because the enums
 * that own them (`SubscriptionTier`, `PartnerAccessLevel`, `ContentMembership`)
 * are TypeScript constructs this file cannot reference.
 *
 * Typed facades re-export every map here with its real type, and are what call
 * sites should import:
 *   - `MembershipTierAccess` in `@optimus/models` (backend)
 *   - `common/utils/MembershipTierAccess` (frontend)
 *   - `ContentMembership` in `@optimus/models` for the content maps
 * Those facades annotate the re-export with a total `Record<SubscriptionTier, T>`,
 * so a tier missing from a map here fails to compile on both sides. The *values*
 * widen to `string`/`number` when they cross that boundary, so they are locked by
 * `MembershipTierAccess.test.ts` instead, which asserts every row explicitly.
 *
 * The domain model these maps encode - one grouping per domain, tiers grouped
 * together being equal in that domain - is documented on
 * `SUBSCRIPTION_TIER_PARTNER_ACCESS` in `MembershipTierAccess`. Source of truth
 * is product's "SN Logic on the Edge platform" page,
 * https://app.notion.com/p/3dca163ffd4d80adbad2c9e01790ad4c.
 *
 * That page carries one chart per domain, and the charts deliberately disagree
 * with each other - a tier can rank differently in each. Do not merge them into a
 * single ranking, and when changing one, change only the domain it belongs to:
 *   - Paid / free               -> `PAID_BY_TIER` here
 *   - Partner access            -> `PARTNER_ACCESS_BY_TIER` here (Partners chart,
 *                                  plus the "Partners with a required form" chart)
 *   - Content entitlement       -> `CONTENT_MEMBERSHIP_BY_TIER` here (Content chart)
 *   - Rebate matrix upsell      -> `REBATE_MATRIX_TIER_CONFIG` in
 *                                  `frontend/solution-org-app/src/components/Dashboard/`
 *                                  `Partner/IndividualPages/PartnerRebateMatrixSection.tsx`
 *                                  (Rebate Matrix chart), kept there because it is a
 *                                  presentation concern - next tier plus its call to action
 *   - Payout methods            -> `components/Dashboard/Wallet/PayoutSettings` (Payout
 *                                  Methods chart), which turns on paper cheque from
 *                                  Association up rather than on a rank
 * Association is the clearest case: it pays, sits on its own partner rung between
 * Edge and Roundtable, reads Foundation content, and is offered Roundtable in the
 * rebate matrix. No single number expresses all four.
 *
 * The older "Partner Logic" page,
 * https://app.notion.com/p/3c7a163ffd4d80e7ad23c5b501dd4933, states "AM = SRT"
 * for partner access. Where the two disagree the SN Logic page wins, confirmed by
 * product on SN-996, which is why Association has its own partner rung here.
 */

/**
 * Whether a tier is a paying membership. Edge is the only free tier: Association
 * is paid, confirmed by product on SN-996, which is why "paid" cannot be
 * expressed as `tier !== 'edge' && tier !== 'am'` (see `PAID_TIERS` in
 * `useHasAccess`) nor as the content membership being above Foundation.
 *
 * This is its own domain, deliberately kept apart from partner access, rebates
 * and content: a paying member is not necessarily entitled to more of any of
 * them (Association pays yet reads Foundation content only).
 */
export const PAID_BY_TIER = {
  edge: false,
  am: true, // Association is a paid membership
  srt: true,
  adv: true,
  advp: true,
  cc: true,
  rcc: true,
  sna: true,
  pin: true,
};

/**
 * The partner access group each tier grants.
 *
 * Association is its own rung between Free and Roundtable, not a synonym for
 * Roundtable: the Partners chart on "SN Logic on the Edge platform" locks an Edge
 * member out of an Association partner ("Upgrade") and an Association member out
 * of a Roundtable partner ("Upgrade"), while every tier from Association up may
 * see an Association partner. The "AM = SRT" equivalence on the older "Partner
 * Logic" page contradicts this; product confirmed on SN-996 that the SN Logic
 * page wins.
 */
export const PARTNER_ACCESS_BY_TIER = {
  edge: 'Free',
  am: 'AM', // Association sits below Roundtable, above Edge
  srt: 'SRT',
  adv: 'Advanced',
  advp: 'Advanced',
  cc: 'Advanced', // Command Center is Advanced, not Alliance
  rcc: 'Advanced', // Regional Command Center is Advanced, not Roundtable
  sna: 'Alliance',
  pin: 'Alliance', // Pinnacle shares Alliance's partner access
};

/**
 * Importance of each partner access group. A member may only see partners whose
 * required group is <= their own.
 */
export const PARTNER_ACCESS_RANK_BY_LEVEL = {
  Free: 1,
  AM: 2,
  SRT: 3,
  Advanced: 4,
  Alliance: 5,
};

/**
 * Partner access groups a member can reach on their own from the Memberships
 * page. Roundtable is the highest tier sold in app, so anything above it needs a
 * sales conversation and a "Contact Sales" call to action rather than "Upgrade".
 * Association is included because the Partners chart shows an Edge member
 * "Upgrade" - not "Contact Sales" - on a partner that requires Association.
 *
 * Not the same set as `salesAssistedTiers` in the member-profile service's
 * `ContactSalesController`, despite the similar name: SN-961 widened that set to
 * every tier except Edge, so it now answers "may this partner be asked about at
 * all", not "does reaching this tier need a salesperson". Do not swap one for the
 * other - routing that controller through `isSalesAssistedTier` would start
 * rejecting the Roundtable and Association members SN-961 deliberately allowed.
 */
export const SELF_SERVE_PARTNER_ACCESS_LEVELS = ['Free', 'AM', 'SRT'];

/** The content membership each tier grants. */
export const CONTENT_MEMBERSHIP_BY_TIER = {
  edge: 'Foundation',
  am: 'Foundation', // Association reads free content only
  srt: 'Performance', // Roundtable
  adv: 'Growth',
  advp: 'Growth', // Advanced
  cc: 'Growth', // Advanced
  rcc: 'Growth', // Advanced
  sna: 'Accelerator',
  pin: 'Accelerator', // Alliance
};

/**
 * Importance of each content membership: the higher the membership, the higher
 * the level. A member may only see content whose required level is <= their own.
 */
export const CONTENT_MEMBERSHIP_LEVEL_BY_NAME = {
  Foundation: 1,
  Performance: 2,
  Growth: 3,
  Accelerator: 4,
};

/**
 * Content memberships a member can reach on their own from the Memberships page.
 * The content-domain twin of `SELF_SERVE_PARTNER_ACCESS_LEVELS`: Performance is
 * granted by Roundtable, the highest tier sold in app, so content tagged Growth
 * or Accelerator needs a sales conversation and a "Contact Sales" call to action
 * rather than "Upgrade to unlock".
 *
 * The Content chart on "SN Logic on the Edge platform" spells this out - Edge,
 * Association and Roundtable members all get "Contact Sales" on Advanced or
 * Alliance content, and "Upgrade to unlock" only on Roundtable content (SN-996).
 */
export const SELF_SERVE_CONTENT_MEMBERSHIPS = ['Foundation', 'Performance'];
